<template>
  <div class="token-editor">
    <div
      ref="ed"
      class="token-editor__area"
      contenteditable="true"
      @input="onInput"
      @keydown="onKeydown"
      @keypress="onKeypress"
      @paste="onPaste"
      @click="onClick"
      @focus="onFocus"
    ></div>
    <transition name="fade">
      <div v-if="showWarning" class="keyboard-warning">
        <v-icon small color="warning">mdi-alert</v-icon>
        <span>Solo puedes escribir números y texto entre comillas (" o ')</span>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "TokenEditor",
  props: {
    value: { type: String, default: "" },
    tokens: { type: Array, default: () => [] },
    noTyping: { type: Boolean, default: false },
    tokenClasses: { type: Object, default: () => ({}) },
    tokenTooltips: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      innerText: "",
      showWarning: false,
      warningTimeout: null,
      isUpdating: false,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        if (!this.isUpdating && v !== this.innerText) {
          this.innerText = v || "";
          this.$nextTick(() => {
            this.render();
          });
        }
      },
    },
    tokens() {
      if (!this.isUpdating) {
        this.$nextTick(() => {
          this.render();
        });
      }
    },
  },
  methods: {
    tokenize(str) {
      // Tokenizador que reconoce palabras compuestas con punto (table.column)
      const parts = [];
      const s = String(str || "");
      let i = 0;

      // IMPORTANTE: Permitir puntos en palabras para soportar table.column
      const isWordStart = (ch) => /[A-Za-z_]/.test(ch);
      const isWordPart = (ch) => /[A-Za-z0-9_.]/.test(ch);

      while (i < s.length) {
        const ch = s[i];

        // Manejo de literales con comillas
        if (ch === '"' || ch === "'") {
          const q = ch;
          let j = i + 1;
          let esc = false;
          while (j < s.length) {
            const cj = s[j];
            if (!esc && cj === q) {
              j++;
              break;
            }
            esc = !esc && cj === "\\";
            j++;
          }
          parts.push({ t: "text", v: s.slice(i, j) });
          i = j;
          continue;
        }

        // Palabras (incluyendo table.column)
        if (isWordStart(ch)) {
          let j = i + 1;
          while (j < s.length && isWordPart(s[j])) j++;
          parts.push({ t: "word", v: s.slice(i, j) });
          i = j;
          continue;
        }

        // Resto como texto
        let j = i + 1;
        while (j < s.length && !(s[j] === '"' || s[j] === "'" || isWordStart(s[j]))) {
          j++;
        }
        parts.push({ t: "text", v: s.slice(i, j) });
        i = j;
      }
      return parts;
    },
    render() {
      const el = this.$refs.ed;
      if (!el) return;

      const raw = this.innerText;
      const set = new Set(this.tokens || []);

      // Guardar posición del cursor
      const sel = window.getSelection();
      let caretPos = 0;
      if (sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(el);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        caretPos = preCaretRange.toString().length;
      }

      // Crear el contenido
      const fr = document.createDocumentFragment();
      let currentPos = 0;

      this.tokenize(raw).forEach((p) => {
        if (p.t === "word" && set.has(p.v)) {
          const span = document.createElement("span");
          const extraClass = this.tokenClasses[p.v] || "";
          span.className = `tk-badge ${extraClass}`.trim();
          span.dataset.token = p.v;
          span.contentEditable = "false";
          const title = this.tokenTooltips[p.v] || "";
          if (title) span.title = title;

          const isLogicalOp = /\b(tk-op)\b/.test(extraClass);
          span.innerHTML = isLogicalOp
            ? `<span class="tk-text">${p.v}</span>`
            : `<span class="tk-text">${p.v}</span><span class="tk-close" data-x="1">×</span>`;
          fr.appendChild(span);
          currentPos += p.v.length;
        } else {
          fr.appendChild(document.createTextNode(p.v));
          currentPos += p.v.length;
        }
      });

      el.innerHTML = "";
      el.appendChild(fr);

      // Restaurar cursor
      this.$nextTick(() => {
        this.setCaretPosition(el, caretPos);
      });
    },
    extractPlain() {
      const el = this.$refs.ed;
      if (!el) return "";
      let out = "";
      el.childNodes.forEach((n) => {
        if (n.nodeType === 3) {
          out += n.nodeValue;
        } else if (n.nodeType === 1 && n.classList.contains("tk-badge")) {
          out += n.dataset.token;
        } else {
          out += n.textContent;
        }
      });
      return out;
    },
    isAllowedChar(char) {
      return /[0-9\s\+\-\*\/\^%\(\),\.\"'<>\=]/.test(char);
    },
    isInQuotes(text, position) {
      let inQuotes = false;
      let quoteChar = null;

      for (let i = 0; i < position; i++) {
        if ((text[i] === '"' || text[i] === "'") && (i === 0 || text[i - 1] !== "\\")) {
          if (!inQuotes) {
            inQuotes = true;
            quoteChar = text[i];
          } else if (text[i] === quoteChar) {
            inQuotes = false;
            quoteChar = null;
          }
        }
      }

      return inQuotes;
    },
    showWarningMessage() {
      this.showWarning = true;
      if (this.warningTimeout) clearTimeout(this.warningTimeout);
      this.warningTimeout = setTimeout(() => {
        this.showWarning = false;
      }, 2500);
    },
    onKeypress(e) {
      if (this.noTyping) {
        const char = e.key;

        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (char.length > 1) return;

        const text = this.extractPlain();
        const position = this.getCaretPosition();

        if (this.isInQuotes(text, position)) return;

        if (!this.isAllowedChar(char)) {
          e.preventDefault();
          this.showWarningMessage();
          return;
        }
      }
    },
    getCaretPosition() {
      const el = this.$refs.ed;
      if (!el) return 0;

      const sel = window.getSelection();
      if (!sel.rangeCount) return 0;

      const range = sel.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(el);
      preCaretRange.setEnd(range.endContainer, range.endOffset);

      return preCaretRange.toString().length;
    },
    onInput(e) {
      this.isUpdating = true;

      const newText = this.extractPlain();
      this.innerText = newText;

      this.$emit("input", newText);

      this.$nextTick(() => {
        this.render();
        this.isUpdating = false;
      });
    },
    onKeydown(e) {
      if (this.noTyping && /^[a-zA-Z]$/.test(e.key)) {
        const text = this.extractPlain();
        const position = this.getCaretPosition();

        if (!this.isInQuotes(text, position)) {
          e.preventDefault();
          this.showWarningMessage();
          return;
        }
      }

      if ((e.key === "Backspace" || e.key === "Delete") && window.getSelection) {
        const sel = window.getSelection();
        if (sel.rangeCount) {
          const range = sel.getRangeAt(0);
          let node = range.startContainer;

          if (node.nodeType === 3) {
            node = node.parentElement;
          }

          if (
            node &&
            node.classList &&
            node.classList.contains("tk-badge") &&
            !node.classList.contains("tk-op")
          ) {
            e.preventDefault();
            node.remove();
            this.isUpdating = true;
            this.innerText = this.extractPlain();
            this.$emit("input", this.innerText);
            this.$nextTick(() => {
              this.isUpdating = false;
            });
          }
        }
      }
    },
    onPaste(e) {
      if (this.noTyping) {
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData).getData("text");

        const hasQuotes = text.includes('"') || text.includes("'");
        const onlyAllowed = /^[0-9\s\+\-\*\/\^%\(\),\.\"'<>\=]+$/.test(text);

        if (hasQuotes || onlyAllowed) {
          document.execCommand("insertText", false, text);
        } else {
          this.showWarningMessage();
        }
      }
    },
    onClick(e) {
      const target = e.target;
      if (target && target.dataset && target.dataset.x === "1") {
        const badge = target.closest(".tk-badge");
        if (badge && !badge.classList.contains("tk-op")) {
          e.preventDefault();
          badge.remove();
          this.isUpdating = true;
          this.innerText = this.extractPlain();
          this.$emit("input", this.innerText);
          this.$nextTick(() => {
            this.isUpdating = false;
          });
        }
      }
    },
    onFocus() {
      this.showWarning = false;
    },
    insertTextAtCursor(textToInsert) {
      const el = this.$refs.ed;
      if (!el) return;
      el.focus();

      const sel = window.getSelection();
      if (!sel.rangeCount) return;

      const range = sel.getRangeAt(0);
      range.deleteContents();

      const textNode = document.createTextNode(textToInsert);
      range.insertNode(textNode);

      range.setStartAfter(textNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);

      this.onInput();
    },
    setCaretPosition(el, pos) {
      let charIndex = 0;
      const range = document.createRange();
      range.setStart(el, 0);
      range.collapse(true);
      const nodeStack = [el];
      let node,
        foundStart = false;

      while (!foundStart && (node = nodeStack.pop())) {
        if (node.nodeType === 3) {
          const nextCharIndex = charIndex + node.length;
          if (!foundStart && pos >= charIndex && pos <= nextCharIndex) {
            range.setStart(node, pos - charIndex);
            foundStart = true;
          }
          charIndex = nextCharIndex;
        } else {
          let i = node.childNodes.length;
          while (i--) {
            nodeStack.push(node.childNodes[i]);
          }
        }
      }

      if (foundStart) {
        range.collapse(true);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        this.placeCaretEnd(el);
      }
    },
    placeCaretEnd(el) {
      try {
        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (e) {
        // Ignorar errores
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.render();
    });
  },
  beforeDestroy() {
    if (this.warningTimeout) clearTimeout(this.warningTimeout);
  },
};
</script>

<style scoped>
.token-editor {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.token-editor__area {
  flex: 1;
  min-height: 140px;
  max-height: 280px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  white-space: pre-wrap;
  word-wrap: break-word;
  outline: none;
  font-family: "SF Mono", "Monaco", "Inconsolata", "Fira Code", monospace;
  font-size: 13px;
  line-height: 1.6;
  background: #ffffff;
  overflow-y: auto;
  overflow-x: hidden;
  transition: all 0.15s ease;
  color: #111827;
}

.token-editor__area:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.token-editor__area:empty::before {
  content: "Escribe tu fórmula...";
  color: #9ca3af;
  pointer-events: none;
}

/* === TOKENS === */
.token-editor__area ::v-deep(.tk-badge) {
  display: inline-flex;
  align-items: center;
  background: #eff6ff;
  color: #1e40af;
  border-radius: 6px;
  padding: 2px 4px 2px 8px;
  margin: 0 2px;
  font-size: 12px;
  font-weight: 500;
  user-select: none;
  vertical-align: middle;
  transition: all 0.15s ease;
  cursor: default;
  border: 1px solid #bfdbfe;
}

.token-editor__area ::v-deep(.tk-badge:hover) {
  background: #dbeafe;
  border-color: #93c5fd;
}

.token-editor__area ::v-deep(.tk-text) {
  padding: 2px 4px;
  display: inline-flex;
  align-items: center;
}

.token-editor__area ::v-deep(.tk-close) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  margin-left: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  border-radius: 4px;
  transition: all 0.15s ease;
  line-height: 1;
  opacity: 0.6;
}

.token-editor__area ::v-deep(.tk-close:hover) {
  background: rgba(30, 64, 175, 0.1);
  opacity: 1;
}

/* Tipos de badge */
.token-editor__area ::v-deep(.tk-def) {
  background: #3b82f6;
  color: #ffffff;
  border-color: #2563eb;
}

.token-editor__area ::v-deep(.tk-def:hover) {
  background: #2563eb;
}

.token-editor__area ::v-deep(.tk-col) {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}

.token-editor__area ::v-deep(.tk-col:hover) {
  background: #e5e7eb;
}

.token-editor__area ::v-deep(.tk-op) {
  background: #fef3c7;
  color: #92400e;
  border-color: #fde68a;
}

/* === WARNING === */
.keyboard-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 12px;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  font-size: 12px;
  color: #92400e;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter {
  opacity: 0;
  transform: translateY(-4px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* === SCROLLBAR === */
.token-editor__area::-webkit-scrollbar {
  width: 6px;
}

.token-editor__area::-webkit-scrollbar-track {
  background: transparent;
}

.token-editor__area::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.token-editor__area::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
